"use client";

import { forwardRef, useEffect, useState } from "react";
import classes from "./provider_selector.module.css";
import CustomSVG from "../common/svg";
import { Dropdown } from "react-bootstrap";
import ChevronDown from '@/assets/svg/icons/chevron-down.svg';
import ChevronUp from '@/assets/svg/icons/chevron-up.svg';
import Info from '@/assets/svg/icons/info.svg';
import Star from '@/assets/svg/icons/star.svg';
import X from '@/assets/svg/icons/x.svg';
import ArrowRight from '@/assets/svg/icons/arrow-right.svg';
import TextInput from "../common/inputs/text_input";
import { getDataForEventCreate, searchAllProviders } from "@/utils/http";
import { useMutation, useQuery } from "@tanstack/react-query";
import CustomBadge from "../common/badges/badge";
import DropdownBase from "../common/dropdown/dropdown_base";
import { useRouter } from "next/navigation";
import CustomTooltip from "../common/tooltip/custom_tooltip";

const LOCATION_PROVIDER_TYPE_ID = 1;

const CustomToggle = forwardRef(function CustomToggle({ onClick, value, setValue, isOpen, onBlur, onFocus, label, placeholder }, ref) {
    return (
        <div ref={ref} onClick={(e) => {e.preventDefault(); onClick(e);}}>
            <TextInput
                className={`w-100 pointer`}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={onBlur}
                onFocus={onFocus}
                type={"text"}
                label={label}
                placeholder={placeholder}
                help_icon={isOpen ? ChevronUp : ChevronDown}
            />
        </div>
    )
});

const CustomToggleCategory = forwardRef(function CustomToggleCategory({ children, onClick, active }, ref) {
    return (
        <div ref={ref} onClick={(e) => {e.preventDefault(); onClick(e);}}>
            <DropdownBase className="pointer d-flex justify-content-between align-self-stretch" active={active} >
                {children}
                <CustomSVG className="stroke-gray-500" SvgFunction={active ? ChevronUp : ChevronDown} height={20} width={20} />
            </DropdownBase>
        </div>
    )
});

export default function ProviderSelector({ children, label, placeholder, providerPreview, setProviderPreview, providerId, setProviderId, newLocationData, handleLocationDataChange, wedding_id, setIsFavorite, onAddCustomProvider }) {
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const [inputIsActive, setInputIsActive] = useState(false);
    const [providerSearch, setProviderSearch] = useState((providerPreview && providerPreview.name) ? providerPreview.name : "");
    const [previousSearch, setPreviousSearch] = useState((providerPreview && providerPreview.name) ? providerPreview.name : "");
    const [selectedProvider, setSelectedProvider] = useState(providerId ? providerId : 0);
    const [filteredProviderList, setFilteredProviderList] = useState(undefined);
    const [categoryIsOpen, setCategoryIsOpen] = useState(false);
    
    const categoryToggle = () => { setCategoryIsOpen(!categoryIsOpen); };
    
    const handleIsOpen = () => {
        if(!inputIsActive || !isOpen) {
            setIsOpen(!isOpen);
        }
    }

    useEffect(() => {
        if(!isOpen && selectedProvider === 0) {
            setSelectedProvider(providerId);
            setProviderSearch(previousSearch)
        }
    }, [isOpen]);

    useEffect(() => {
        setProviderSearch((providerPreview && providerPreview.name) ? providerPreview.name : "");
        setPreviousSearch((providerPreview && providerPreview.name) ? providerPreview.name : "");
    }, [providerPreview])

    const { data: starting_values } = useQuery({
        queryKey: ['events_create', [wedding_id]],
        queryFn: () => getDataForEventCreate({wedding_id: wedding_id})
    });

    const { mutate: searchProvider } = useMutation({
        mutationFn: searchAllProviders,
        onSuccess: (response) => {
            if(response.success) setFilteredProviderList(response.response);
            else setFilteredProviderList(undefined);
        }
    });

    const providerSelectHandler = (provider) => {
        setSelectedProvider(provider.id);
        setProviderId(provider.id);

        handleLocationDataChange("address_city", provider.address_city);
        handleLocationDataChange("address_street", provider.address_street);
        handleLocationDataChange("address_zip", provider.address_zip);
        setProviderSearch(provider.name);
        setPreviousSearch(provider.name);

        setProviderPreview(provider);
    }

    const addNewToList = () => {
        onAddCustomProvider?.();
        const params = new URLSearchParams({
            search: providerSearch,
            program_location_flow: "1"
        });
        router.push(`/szervezom/${wedding_id}/szolgaltatok?${params.toString()}`);
    }

    const handleProviderSearch = (val) => {
        setProviderSearch(val);
        setSelectedProvider(0);

        searchProvider({
            wedding_id: wedding_id,
            params: {
                content: val,
                type_id: LOCATION_PROVIDER_TYPE_ID
            }
        })
    }

    const removeSelectedProvider = () => {
        setProviderId(0);
        setSelectedProvider(0);
        setProviderSearch("");
    }

    useEffect(() => {
        if(providerId !== undefined && starting_values) {
            setIsFavorite(providerId !== 0 && !(starting_values.favourites.map(f => f.id).includes(providerId)));
        }
    }, [providerId, starting_values])

    if(!starting_values) return <></>

    const locationFavourites = starting_values.favourites.filter(provider => Number(provider.provider_type_id) === LOCATION_PROVIDER_TYPE_ID);

    return <div className="d-flex align-self-stretch flex-column gap-4">
        <div className="d-flex align-items-center gap-2">
            <Dropdown show={isOpen} onToggle={handleIsOpen} style={{width: "100%"}}>
                <Dropdown.Toggle as={CustomToggle} value={providerSearch} setValue={handleProviderSearch} isOpen={isOpen} setIsOpen={setIsOpen} onBlur={() => setInputIsActive(false)} onFocus={() => setInputIsActive(true)} label={label} placeholder={placeholder} />
                <Dropdown.Menu className={`${classes["dropdown_menu"]}`} style={{width: "100%"}}>
                    {(providerSearch !== "" && filteredProviderList)
                        ? <ProviderListElements favourites={filteredProviderList.favourites} providers={filteredProviderList.providers} providerSelectHandler={providerSelectHandler} search_val={providerSearch} hide_search_empty={true} show_custom_tag />
                        : <ProviderListElements favourites={locationFavourites} providers={starting_values.default_providers} providerSelectHandler={providerSelectHandler} search_val={providerSearch} hide_search_empty={true} show_custom_tag />
                    }
                    {(providerSearch !== "" && filteredProviderList?.favourites.length === 0  && filteredProviderList?.providers.length === 0  && selectedProvider === 0) && 
                        <Dropdown.Item className="d-flex flex-row align-self-stretch justify-content-between align-items-center" onClick={addNewToList}>
                            <p className="margin-0 padding-0 color-gray-900 text-md-regular text-truncate ">
                                {providerSearch}
                            </p>
                            <div className="d-flex flex-row gap-2 align-items-center overflow-auto flex-shrink-0">
                                <CustomBadge size="sm" color={"primary"} >Nincs találat</CustomBadge>

                                <CustomSVG className={`stroke-gray-500 pointer`}  data-tooltip-id="provider-selector-tooltip" SvgFunction={Info} height={20} width={20} />
                                <CustomTooltip id="provider-selector-tooltip" theme={"dark"} opacity={1} style={{maxWidth: 500}} place_type={"bottom"} >
                                    <div className='text-xs-semibold'>A programszerkesztés a helyszín felvétele után automatikusan folytatódik.</div>
                                </CustomTooltip>

                                <p className="margin-0 padding-0 padding-left-2 color-gray-500 text-md-medium">
                                    Egyedi helyszín felvétele
                                </p>
                                <CustomSVG className={`stroke-gray-500`} SvgFunction={ArrowRight} height={20} width={20} />
                            </div>
                        </Dropdown.Item>
                    }
                    {(providerSearch === "" && selectedProvider === 0) && <Dropdown.Item className="d-flex flex-row align-self-stretch justify-content-between align-items-center">
                        <p className="margin-0 padding-0 color-gray-900 text-md-regular text-truncate ">
                            Kezdd el beírni a helyszín nevét a kereséshez...
                        </p>
                    </Dropdown.Item>}
                </Dropdown.Menu>
            </Dropdown>
            { providerId > 0 && 
                (
                    <>
                        <CustomSVG data-tooltip-id={`provider-selector-tooltip-${providerId}`}  className="stroke-gray-900 pointer flex-shrink-0" onClick={() => removeSelectedProvider()} SvgFunction={X} height={32} width={32} />
                        <CustomTooltip id={`provider-selector-tooltip-${providerId}`} theme={"dark"} opacity={1} style={{maxWidth: 500}} place_type={"bottom"} >
                            <div className='text-xs-semibold'>Kiválasztás törlése</div>
                        </CustomTooltip>
                    </>
                )
            }
        </div>
        {children}
    </div>
}

export function ProviderListElements({favourites, providers, providerSelectHandler, hide_when_empty, search_val = "", hide_search_empty = false, has_title = true, show_custom_tag = false}) {
    return <>
        {favourites && favourites.length > 0 && <div className={`d-flex flex-column align-self-stretch ${classes["favourites-container"]}`}>
            {has_title && <h3 className={`margin-0 text-xs-regular color-gray-500 text-uppercase ${classes["dropdown-title"]}`}>Kedvencek</h3>}
            {favourites.map(f => <Dropdown.Item key={`favourite-${f.id}`} className="d-flex flex-row align-self-stretch justify-content-between align-items-center" onClick={() => providerSelectHandler(f)}>
                <div className="d-flex flex-row gap-2 align-items-center overflow-auto">
                    <CustomSVG className={`stroke-button-navbar fill-button-navbar flex-shrink-0`} SvgFunction={Star} height={20} width={20} />
                    <p className="margin-0 padding-0 color-gray-900 text-md-regular text-truncate ">
                        {f.name}
                    </p>
                </div>
                {show_custom_tag
                    ? <div className="d-flex flex-row gap-1 align-items-center">
                        {f.is_custom && <CustomBadge size="sm" color={"primary"} >Egyéni</CustomBadge>}
                        <p className="margin-0 padding-0 padding-left-2 color-gray-400 text-md-regular">
                            {f.address_city}
                        </p>
                    </div>
                    : <p className="margin-0 padding-0 padding-left-2 color-gray-400 text-md-regular">
                        {f.address_city}
                    </p>
                }
            </Dropdown.Item>)}
        </div>}
        {(!hide_search_empty || search_val.trim()) && providers && (!hide_when_empty || providers.length > 0) && <div className="d-flex flex-column align-self-stretch">
            {has_title && <h3 className={`margin-0 text-xs-regular color-gray-500 text-uppercase ${classes["dropdown-title"]}`}>Továbbiak</h3>}
            {providers.map(f => <Dropdown.Item key={`favourite-${f.id}`} className="d-flex flex-row align-self-stretch justify-content-between align-items-center" onClick={() => providerSelectHandler(f)}>
                <p className="margin-0 padding-0 color-gray-900 text-md-regular text-truncate ">
                    {f.name}
                </p>
                {show_custom_tag
                    ? <div className="d-flex flex-row gap-1 align-items-center">
                        {f.is_custom && <CustomBadge size="sm" color={"primary"} >Egyéni</CustomBadge>}
                        <p className="margin-0 padding-0 padding-left-2 color-gray-400 text-md-regular">
                            {f.address_city}
                        </p>
                    </div>
                    : <p className="margin-0 padding-0 padding-left-2 color-gray-400 text-md-regular">
                        {f.address_city}
                    </p>
                }
            </Dropdown.Item>)}
        </div>}
    </>
}